import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  DollarSign,
  BarChart3,
  FileCheck,
  Target,
  Eye,
  TrendingUp,
  Scale,
  PieChart as PieChartIcon
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  RadialBarChart,
  RadialBar,
  PieChart,
  Pie
} from "recharts";

const MetricCard = ({ label, value, subtext, icon: Icon, color = "slate" }) => {
  const colorClasses = {
    emerald: "text-emerald-600 bg-emerald-50 border-emerald-200",
    amber: "text-amber-600 bg-amber-50 border-amber-200",
    red: "text-red-600 bg-red-50 border-red-200",
    blue: "text-blue-600 bg-blue-50 border-blue-200",
    purple: "text-purple-600 bg-purple-50 border-purple-200",
    slate: "text-slate-600 bg-slate-50 border-slate-200",
  };
  
  return (
    <div className={`border rounded-md p-3 ${colorClasses[color]}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} />
        <span className="text-sm uppercase tracking-wider font-bold">{label}</span>
      </div>
      <div className="text-xl font-bold">{value}</div>
      {subtext && <div className="text-sm opacity-70">{subtext}</div>}
    </div>
  );
};

const RiskBadge = ({ level }) => {
  const styles = {
    low: "bg-emerald-100 text-emerald-700",
    medium: "bg-amber-100 text-amber-700",
    high: "bg-red-100 text-red-700",
  };
  return (
    <Badge className={`text-sm ${styles[level] || styles.medium}`}>
      {level?.toUpperCase()} RISK
    </Badge>
  );
};

const Section = ({ title, icon: Icon, children, color = "slate" }) => {
  const bgColors = {
    slate: "bg-slate-50",
    purple: "bg-purple-50",
    amber: "bg-amber-50",
    emerald: "bg-emerald-50",
    red: "bg-red-50",
  };
  
  return (
    <div className={`rounded-md border border-slate-200 overflow-hidden`}>
      <div className={`${bgColors[color]} px-4 py-2 border-b border-slate-200 flex items-center gap-2`}>
        <Icon size={14} className="text-slate-600" />
        <span className="text-base font-bold uppercase tracking-wider text-slate-700">{title}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
};

// Insurability Score Gauge
const InsurabilityGauge = ({ score, grade }) => {
  const gaugeData = [
    { name: 'Score', value: score, fill: score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444' }
  ];
  
  return (
    <div className="flex flex-col items-center">
      <ResponsiveContainer width={180} height={120}>
        <RadialBarChart 
          cx="50%" 
          cy="100%" 
          innerRadius="60%" 
          outerRadius="100%" 
          barSize={12} 
          data={gaugeData}
          startAngle={180} 
          endAngle={0}
        >
          <RadialBar
            background={{ fill: '#e2e8f0' }}
            dataKey="value"
            cornerRadius={6}
          />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="text-center -mt-8">
        <div className="text-3xl font-bold text-slate-800">{score}</div>
        <Badge className={`mt-1 ${score >= 80 ? 'bg-emerald-100 text-emerald-700' : score >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
          Grade {grade}
        </Badge>
      </div>
    </div>
  );
};

// Claim Probability Pie Chart
const ClaimProbabilityChart = ({ data }) => {
  if (!data) return null;
  
  const chartData = [
    { name: 'Mechanical', value: parseFloat(data.mechanical_failure_prob_percent) || 0, fill: '#ef4444' },
    { name: 'Accident', value: parseFloat(data.accident_related_prob_percent) || 0, fill: '#f59e0b' },
    { name: 'No Claim', value: Math.max(0, 100 - (parseFloat(data.total_claim_prob_percent) || 0)), fill: '#10b981' },
  ];
  
  return (
    <ResponsiveContainer width="100%" height={180}>
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          innerRadius={40}
          outerRadius={70}
          paddingAngle={2}
          dataKey="value"
          label={({ name, value }) => value > 5 ? `${name}: ${value}%` : ''}
          labelLine={false}
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.fill} />
          ))}
        </Pie>
        <Tooltip formatter={(value) => `${value}%`} />
      </PieChart>
    </ResponsiveContainer>
  );
};

export default function InsuranceAnalysis({ data }) {
  if (!data || data.error) return null;
  
  const {
    executive_summary,
    insurability_score,
    insurability_grade,
    idv_calculation,
    risk_quantification,
    fraud_risk_model,
    premium_model,
    claims_impact_analysis,
  } = data;

  const getScoreColor = (score) => {
    if (score >= 80) return "emerald";
    if (score >= 60) return "amber";
    return "red";
  };

  // Prepare IDV trend data for chart
  const idvTrendData = idv_calculation?.idv_trend_data?.map(item => ({
    year: item.year,
    standardIdv: parseFloat(String(item.standard_idv).replace('%', '')) || 0,
    conditionIdv: parseFloat(String(item.condition_idv).replace('%', '')) || 0,
  })) || [];

  // Prepare severity distribution data
  const severityData = risk_quantification?.expected_claim_value?.severity_distribution ? [
    { name: 'Minor', value: parseFloat(String(risk_quantification.expected_claim_value.severity_distribution.minor).replace('%', '')) || 0, fill: '#10b981' },
    { name: 'Moderate', value: parseFloat(String(risk_quantification.expected_claim_value.severity_distribution.moderate).replace('%', '')) || 0, fill: '#f59e0b' },
    { name: 'Major', value: parseFloat(String(risk_quantification.expected_claim_value.severity_distribution.major).replace('%', '')) || 0, fill: '#ef4444' },
  ] : [];

  return (
    <Card className="border border-slate-200 shadow-none rounded-sm overflow-hidden" data-testid="insurance-analysis-card">
      <div className="border-b border-slate-200 p-3 bg-gradient-to-r from-purple-50 to-pink-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield size={16} className="text-purple-600" />
          <span className="text-base font-bold uppercase tracking-wider text-purple-800">
            Insurance Company Analysis
          </span>
          <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-sm">
            UNDERWRITING VIEW
          </Badge>
        </div>
      </div>
      
      <CardContent className="p-4 space-y-4">
        {/* Executive Summary + Insurability Gauge */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            {executive_summary && (
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-md h-full">
                <div className="flex items-center gap-2 mb-2">
                  <FileCheck size={14} className="text-purple-600" />
                  <span className="text-base font-bold text-purple-800 uppercase">Underwriter Summary</span>
                </div>
                <p className="text-sm text-slate-700">{executive_summary}</p>
              </div>
            )}
          </div>
          <div className="flex justify-center items-center bg-slate-50 rounded-md border border-slate-200 p-2">
            <InsurabilityGauge score={insurability_score || 0} grade={insurability_grade || 'N/A'} />
          </div>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard 
            label="IDV Adjust" 
            value={`${(idv_calculation?.final_idv_recommendation_percent || 0) > 0 ? '+' : ''}${idv_calculation?.final_idv_recommendation_percent || 0}%`}
            subtext="vs Standard"
            icon={Scale}
            color={(idv_calculation?.final_idv_recommendation_percent || 0) >= 0 ? "emerald" : "amber"}
          />
          <MetricCard 
            label="Loss Ratio" 
            value={`${risk_quantification?.condition_adjusted_loss_ratio_percent || 0}%`}
            subtext="Adjusted"
            icon={BarChart3}
            color={getScoreColor(100 - (parseFloat(risk_quantification?.condition_adjusted_loss_ratio_percent) || 50))}
          />
          <MetricCard 
            label="Premium" 
            value={`×${premium_model?.final_premium_multiplier || 1.0}`}
            subtext="Multiplier"
            icon={DollarSign}
            color={(parseFloat(premium_model?.final_premium_multiplier) || 1.0) <= 1.1 ? "emerald" : "amber"}
          />
          <MetricCard 
            label="Fraud Risk" 
            value={`${fraud_risk_model?.pre_existing_damage_score || 0}`}
            subtext={`/100 - ${fraud_risk_model?.staged_claim_vulnerability || 'N/A'}`}
            icon={Eye}
            color={(fraud_risk_model?.pre_existing_damage_score || 0) <= 30 ? "emerald" : 
                   (fraud_risk_model?.pre_existing_damage_score || 0) <= 60 ? "amber" : "red"}
          />
        </div>

        {/* IDV Assessment with Chart */}
        <Section title="Insured Declared Value (IDV) Analysis" icon={Scale} color="purple">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* IDV Metrics */}
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="p-3 bg-slate-50 rounded-md text-center">
                  <div className="text-xs text-slate-500 uppercase">Base IDV</div>
                  <div className="text-lg font-bold text-slate-700">{idv_calculation?.base_idv_percent || 0}%</div>
                </div>
                <div className="p-3 bg-purple-50 rounded-md text-center">
                  <div className="text-xs text-purple-600 uppercase">Condition Adj.</div>
                  <div className="text-lg font-bold text-purple-700">
                    {(idv_calculation?.condition_adjustment_percent || 0) > 0 ? '+' : ''}
                    {idv_calculation?.condition_adjustment_percent || 0}%
                  </div>
                </div>
                <div className="p-3 bg-emerald-50 rounded-md text-center">
                  <div className="text-xs text-emerald-600 uppercase">Final IDV</div>
                  <div className="text-lg font-bold text-emerald-700">{idv_calculation?.final_idv_recommendation_percent || 0}%</div>
                </div>
              </div>
              
              {idv_calculation?.idv_justification && (
                <p className="text-sm text-slate-600 italic p-2 bg-slate-50 rounded-md">
                  {idv_calculation.idv_justification}
                </p>
              )}
            </div>

            {/* IDV Trend Chart */}
            {idvTrendData.length > 0 && (
              <div className="h-56">
                <div className="text-sm text-slate-500 uppercase mb-2 text-center">IDV Trend (3 Years)</div>
                <ResponsiveContainer width="100%" height="90%">
                  <LineChart data={idvTrendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="year" 
                      tick={{ fontSize: 12, fill: '#64748b' }}
                      tickFormatter={(value) => `Y${value}`}
                    />
                    <YAxis 
                      tick={{ fontSize: 12, fill: '#64748b' }}
                      tickFormatter={(value) => `${value}%`}
                      domain={[0, 100]}
                    />
                    <Tooltip formatter={(value) => [`${value}%`, '']} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line 
                      type="monotone" 
                      dataKey="standardIdv" 
                      stroke="#94a3b8" 
                      strokeWidth={2}
                      name="Standard IDV"
                      dot={{ fill: '#94a3b8', r: 4 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="conditionIdv" 
                      stroke="#8b5cf6" 
                      strokeWidth={2}
                      name="Condition IDV"
                      dot={{ fill: '#8b5cf6', r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </Section>

        {/* Risk Quantification with Charts */}
        <Section title="Risk Quantification Model" icon={AlertTriangle} color="amber">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Risk Metrics */}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-50 rounded-md">
                  <div className="text-xs text-slate-500 uppercase mb-1">Base Loss Ratio</div>
                  <div className="text-xl font-bold text-slate-700">{risk_quantification?.base_loss_ratio_percent || 0}%</div>
                </div>
                <div className="p-3 bg-amber-50 rounded-md">
                  <div className="text-xs text-amber-600 uppercase mb-1">Adjusted Loss Ratio</div>
                  <div className="text-xl font-bold text-amber-700">{risk_quantification?.condition_adjusted_loss_ratio_percent || 0}%</div>
                </div>
              </div>
              
              <div className="p-3 bg-red-50 rounded-md">
                <div className="text-xs text-red-600 uppercase mb-1">Total Claim Probability</div>
                <div className="text-2xl font-bold text-red-700">
                  {risk_quantification?.claim_probability_model?.total_claim_prob_percent || 0}%
                </div>
                <div className="flex gap-2 mt-2">
                  <Badge className="bg-red-100 text-red-700 text-xs">
                    Mechanical: {risk_quantification?.claim_probability_model?.mechanical_failure_prob_percent || 0}%
                  </Badge>
                  <Badge className="bg-amber-100 text-amber-700 text-xs">
                    Accident: {risk_quantification?.claim_probability_model?.accident_related_prob_percent || 0}%
                  </Badge>
                </div>
              </div>
              
              {risk_quantification?.expected_claim_value?.average_claim_percent_of_idv && (
                <div className="p-2 bg-slate-50 rounded-md">
                  <div className="text-xs text-slate-500">Average Claim (% of IDV)</div>
                  <div className="text-lg font-bold text-slate-700">{risk_quantification.expected_claim_value.average_claim_percent_of_idv}</div>
                </div>
              )}
            </div>

            {/* Claim Probability & Severity Charts */}
            <div className="space-y-2">
              <div className="text-sm text-slate-500 uppercase text-center">Claim Severity Distribution</div>
              {severityData.length > 0 && (
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={severityData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}%`}
                      labelLine={false}
                    >
                      {severityData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `${value}%`} />
                    <Legend wrapperStyle={{ fontSize: '10px' }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </Section>

        {/* Fraud Risk Model */}
        <Section title="Fraud Detection & Prevention" icon={Eye} color="red">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Fraud Metrics */}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-50 rounded-md text-center">
                  <div className="text-xs text-slate-500 uppercase">Pre-existing Damage Score</div>
                  <div className={`text-2xl font-bold ${
                    (fraud_risk_model?.pre_existing_damage_score || 0) <= 30 ? 'text-emerald-600' :
                    (fraud_risk_model?.pre_existing_damage_score || 0) <= 60 ? 'text-amber-600' : 'text-red-600'
                  }`}>
                    {fraud_risk_model?.pre_existing_damage_score || 0}/100
                  </div>
                </div>
                <div className="p-3 bg-slate-50 rounded-md text-center">
                  <div className="text-xs text-slate-500 uppercase">Detection Confidence</div>
                  <div className="text-2xl font-bold text-emerald-600">
                    {fraud_risk_model?.detection_confidence_percent || 0}%
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Badge className={`${
                  fraud_risk_model?.staged_claim_vulnerability === 'low' ? 'bg-emerald-100 text-emerald-700' :
                  fraud_risk_model?.staged_claim_vulnerability === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                }`}>
                  Staged Claim: {fraud_risk_model?.staged_claim_vulnerability || 'N/A'}
                </Badge>
                <Badge className={`${
                  fraud_risk_model?.inflation_risk === 'low' ? 'bg-emerald-100 text-emerald-700' :
                  fraud_risk_model?.inflation_risk === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                }`}>
                  Inflation Risk: {fraud_risk_model?.inflation_risk || 'N/A'}
                </Badge>
              </div>
            </div>

            {/* Red Flags */}
            <div>
              {fraud_risk_model?.red_flags && fraud_risk_model.red_flags.length > 0 && (
                <div>
                  <div className="text-sm text-slate-500 uppercase mb-2">Red Flags Detected</div>
                  <div className="space-y-1">
                    {fraud_risk_model.red_flags.map((flag, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm text-red-600 bg-red-50 p-2 rounded-md">
                        <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                        {flag}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(!fraud_risk_model?.red_flags || fraud_risk_model.red_flags.length === 0) && (
                <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 p-3 rounded-md">
                  <CheckCircle size={16} />
                  <span className="text-sm font-medium">No significant fraud indicators detected</span>
                </div>
              )}
            </div>
          </div>
        </Section>

        {/* Premium Recommendations */}
        <Section title="Premium Pricing Model" icon={DollarSign} color="purple">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div className="p-3 bg-slate-50 rounded-md text-center">
              <div className="text-xs text-slate-500 uppercase">Base Factor</div>
              <div className="text-xl font-bold text-slate-700">×{premium_model?.base_premium_factor || 1.0}</div>
            </div>
            <div className="p-3 bg-amber-50 rounded-md text-center">
              <div className="text-xs text-amber-600 uppercase">Condition Loading</div>
              <div className="text-xl font-bold text-amber-700">×{premium_model?.condition_loading_factor || 1.0}</div>
            </div>
            <div className="p-3 bg-purple-50 rounded-md text-center">
              <div className="text-xs text-purple-600 uppercase">Final Multiplier</div>
              <div className="text-xl font-bold text-purple-700">×{premium_model?.final_premium_multiplier || 1.0}</div>
            </div>
            <div className="p-3 bg-blue-50 rounded-md text-center">
              <div className="text-xs text-blue-600 uppercase">Deductible</div>
              <div className="text-xl font-bold text-blue-700">{premium_model?.recommended_deductible_percent || 0}%</div>
            </div>
          </div>
          
          {premium_model?.premium_justification && (
            <p className="text-sm text-slate-600 italic p-2 bg-slate-50 rounded-md mb-3">
              {premium_model.premium_justification}
            </p>
          )}
        </Section>

        {/* Claims Impact Analysis */}
        <Section title="Portfolio Impact Analysis" icon={Target} color="emerald">
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-emerald-50 rounded-md text-center border border-emerald-200">
              <div className="text-sm text-emerald-600 uppercase">Preventable Claims</div>
              <div className="text-2xl font-bold text-emerald-700">
                {claims_impact_analysis?.preventable_claims_percent || 0}%
              </div>
              <div className="text-xs text-emerald-500">Claims this inspection prevents</div>
            </div>
            <div className="p-3 bg-blue-50 rounded-md text-center border border-blue-200">
              <div className="text-sm text-blue-600 uppercase">Early Detection Savings</div>
              <div className="text-2xl font-bold text-blue-700">
                {claims_impact_analysis?.early_detection_savings_percent || 0}%
              </div>
              <div className="text-xs text-blue-500">Cost reduction</div>
            </div>
            <div className="p-3 bg-purple-50 rounded-md text-center border border-purple-200">
              <div className="text-sm text-purple-600 uppercase">Loss Ratio Impact</div>
              <div className="text-2xl font-bold text-purple-700">
                {claims_impact_analysis?.portfolio_loss_ratio_impact || 'N/A'}
              </div>
              <div className="text-xs text-purple-500">Portfolio improvement</div>
            </div>
          </div>
        </Section>
      </CardContent>
    </Card>
  );
}
